import { AdminListPageConfig, AdminListPageRenderer, useAdminListPage } from 'hooks/useAdminListPage';
import { adminIndexClients } from 'services/api/admin/clients';
import {
  type AdminCoordinationRequest,
  adminCoordinationRequestsQueryKeys,
  adminIndexCoordinationRequests,
  type CoordinationRequestStatus,
} from 'services/api/admin/coordinationRequests';

const STATUS_LABELS: Record<CoordinationRequestStatus, string> = {
  pending: 'Pending',
  auto_completed: 'Auto-Completed',
  completed: 'Completed',
  auto_dismissed: 'Auto-Dismissed',
  dismissed: 'Dismissed',
};

const STATUS_OPTIONS = (Object.keys(STATUS_LABELS) as CoordinationRequestStatus[]).map((value) => ({
  id: value,
  name: STATUS_LABELS[value],
}));

const SOURCE_OPTIONS = [
  { id: 'integration', name: 'Integration' },
  { id: 'user', name: 'User' },
];

const coordinationRequestsConfig: AdminListPageConfig<AdminCoordinationRequest> = {
  title: 'Coordination Requests',
  columnStorageKey: 'admin.coordinationRequests',
  queryKey: adminCoordinationRequestsQueryKeys.index(),
  fetchFn: (params, signal) => adminIndexCoordinationRequests(params, signal),
  fetchParams: { defaultSort: 'created_at desc' },
  showResultCount: true,

  filters: [
    {
      key: 'status',
      label: 'Status',
      type: 'multi_select',
      staticOptions: STATUS_OPTIONS,
    },
    {
      key: 'client',
      label: 'Client',
      type: 'multi_select',
      fetchConfig: {
        queryKey: ['adminClients', 'coordinationRequests', 'filter'],
        fetchFn: adminIndexClients,
        params: { sortBy: 'name asc' },
      },
    },
    {
      key: 'source',
      label: 'Source',
      type: 'multi_select',
      staticOptions: SOURCE_OPTIONS,
    },
    {
      key: 'created_at',
      label: 'Date Range',
      type: 'date_range',
      defaultRange: 90,
    },
  ],

  columns: [
    {
      id: 'patient_name',
      header: 'Patient Name',
      render: (row) => row.patientName || '—',
    },
    {
      id: 'client',
      header: 'Client',
      render: (row) => row.client?.name || '—',
    },
    {
      id: 'status',
      header: 'Status',
      render: (row) => STATUS_LABELS[row.status] || row.status || '—',
    },
    {
      id: 'submitted_by',
      header: 'Submitted By',
      render: (row) => row.submittedBy || '—',
    },
    {
      id: 'user',
      header: 'User',
      render: (row) => row.user || '—',
    },
  ],
};

export default function CoordinationRequests() {
  const state = useAdminListPage(coordinationRequestsConfig);

  return <AdminListPageRenderer config={coordinationRequestsConfig} state={state} />;
}
